import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, RotateCcw, ShieldCheck, Sparkles } from "lucide-react";
import { useApp } from "../context/AppContext";
import { categories, heroImage, heroSecondary, editorialImage, lookbookWoman, lookbookMan } from "../mock";
import ProductCard from "../components/ProductCard";
import API from "../api";

const Home = () => {
  const { t } = useApp();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    API.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const trending = products.filter((p) => p.rating >= 4.6).slice(0, 8);
  const newItems = products.filter((p) => p.isNew).slice(0, 4);

  return (
    <div className="fade-up">
      <section className="relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12 grid md:grid-cols-2 gap-4 md:gap-8 items-stretch">
          <div className="relative rounded-sm overflow-hidden min-h-[420px] md:min-h-[560px] group">
            <img src={heroImage} alt="hero" className="img-zoom absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 md:p-12 text-white">
              <span className="text-xs uppercase tracking-[0.2em] mb-3 text-white/80">{t("newArrivals")}</span>
              <h1 className="font-serif-lux text-4xl md:text-6xl leading-[1.05] max-w-md">{t("heroTitle")}</h1>
              <p className="mt-4 text-sm md:text-base text-white/85 max-w-sm leading-relaxed">{t("heroSubtitle")}</p>
              <Link to="/c/women" className="mt-6 inline-flex items-center gap-2 bg-white text-[#1b1b1b] px-7 py-3.5 text-sm tracking-wide w-fit hover:bg-[#b08d57] hover:text-white transition-colors">
                {t("shopNow")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
          <div className="grid grid-rows-2 gap-4">
            <SplitCard image={heroSecondary} label={t("shopWomen")} to="/c/women" />
            <SplitCard image={categories[1].image} label={t("shopMen")} to="/c/men" />
          </div>
        </div>
      </section>

      <section className="border-y border-border bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[{ icon: Truck, text: t("deliveryInfo").split(".")[0] }, { icon: RotateCcw, text: t("delivery") }, { icon: ShieldCheck, text: "Paiement sécurisé" }, { icon: Sparkles, text: t("editorialTitle") }].map((s, i) => (
            <div key={i} className="flex items-center gap-3 text-sm text-[#333]">
              <s.icon className="h-5 w-5 text-[#b08d57] flex-shrink-0" strokeWidth={1.5} />
              <span className="leading-tight">{s.text}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
        <SectionHead title={t("categoriesTitle")} />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {categories.map((c) => (
            <Link key={c.id} to={`/c/${c.id}`} className="group relative overflow-hidden rounded-sm aspect-[3/4]">
              <img src={c.image} alt={t(c.key)} className="img-zoom w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
                <span className="font-serif-lux text-lg">{t(c.key)}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-8 pb-16">
        <SectionHead title={t("trendingTitle")} link="/c/all" linkLabel={t("viewAll")} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8">
          {trending.map((p) => (<ProductCard key={p.id} product={p} />))}
        </div>
      </section>

      <section className="bg-[#f2ede4]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div className="relative rounded-sm overflow-hidden aspect-[4/3] md:aspect-[4/5] group">
            <img src={editorialImage} alt="editorial" className="img-zoom w-full h-full object-cover" />
          </div>
          <div className="max-w-md">
            <span className="text-xs uppercase tracking-[0.2em] text-[#b08d57]">Amourduluxe</span>
            <h2 className="font-serif-lux text-4xl md:text-5xl mt-3 leading-tight">{t("editorialTitle")}</h2>
            <p className="mt-5 text-[15px] text-[#444] leading-relaxed">{t("editorialText")}</p>
            <Link to="/c/accessories" className="mt-7 inline-flex items-center gap-2 border border-[#1b1b1b] px-7 py-3.5 text-sm tracking-wide hover:bg-[#1b1b1b] hover:text-white transition-colors">
              {t("discover")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
        <SectionHead title={t("lookbookTitle")} />
        <p className="-mt-4 mb-8 text-[15px] text-[#555] max-w-lg leading-relaxed">{t("lookbookText")}</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/c/women" className="relative rounded-sm overflow-hidden group aspect-[4/5] md:aspect-[16/10]">
            <img src={lookbookWoman} alt="lookbook femme" className="img-zoom absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="relative h-full flex items-end p-6 md:p-8">
              <span className="text-white font-serif-lux text-2xl md:text-3xl flex items-center gap-2 group-hover:gap-3 transition-all">{t("shopWomen")} <ArrowRight className="h-5 w-5" strokeWidth={1.5} /></span>
            </div>
          </Link>
          <Link to="/c/men" className="relative rounded-sm overflow-hidden group aspect-[4/5] md:aspect-[16/10]">
            <img src={lookbookMan} alt="lookbook homme" className="img-zoom absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="relative h-full flex items-end p-6 md:p-8">
              <span className="text-white font-serif-lux text-2xl md:text-3xl flex items-center gap-2 group-hover:gap-3 transition-all">{t("shopMen")} <ArrowRight className="h-5 w-5" strokeWidth={1.5} /></span>
            </div>
          </Link>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16">
        <SectionHead title={t("newArrivals")} link="/c/all" linkLabel={t("viewAll")} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8">
          {newItems.map((p) => (<ProductCard key={p.id} product={p} />))}
        </div>
      </section>
    </div>
  );
};

const SplitCard = ({ image, label, to }) => (
  <Link to={to} className="relative rounded-sm overflow-hidden group min-h-[200px]">
    <img src={image} alt={label} className="img-zoom absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
    <div className="relative h-full flex items-end p-6">
      <span className="text-white font-serif-lux text-2xl flex items-center gap-2 group-hover:gap-3 transition-all">{label} <ArrowRight className="h-5 w-5" strokeWidth={1.5} /></span>
    </div>
  </Link>
);

const SectionHead = ({ title, link, linkLabel }) => (
  <div className="flex items-end justify-between mb-8">
    <div>
      <div className="h-px w-10 bg-[#b08d57] mb-4" />
      <h2 className="font-serif-lux text-3xl md:text-4xl text-[#1b1b1b]">{title}</h2>
    </div>
    {link && (<Link to={link} className="text-sm tracking-wide text-[#333] hover:text-[#b08d57] transition-colors flex items-center gap-1.5 pb-1">{linkLabel} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link>)}
  </div>
);

export default Home;
